import { apiBaseOrigin } from '../../../shared/config/api-origin';
import { createMonitoringRequest } from './http';

export interface Forecast {
  id: string;
  sourceMode: 'LIVE' | 'DEMO';
  factType: 'WEATHER_FORECAST';
  region: {
    instanceId: string;
    sourceId?: string;
    regionId: string;
    displayName: string;
    latitude: number;
    longitude: number;
    applicability: string;
  };
  provider: string;
  product: string;
  endpoint: string;
  pointsUrl: string;
  gridDataUrl: string;
  gridIdentity: string;
  providerTimeZone?: string;
  issuedAt: string;
  validFrom: string;
  validUntil: string;
  retrievedAt: string;
  revision: string;
  rawSha256: string;
  discoverySha256: string;
  spatialType: 'GRID_FORECAST';
  polygon: number[][];
  quality: string;
  metrics: { name: string; value: number | null; unit: string; quality: string }[];
}
export interface Evaluation {
  id: string;
  instanceId?: string;
  inputMode: string;
  regionId: string;
  result: string;
  explanation: string;
  forecastIds: string[];
  observationIds: string[];
  sourceLineage: string[];
  corroboration: string;
  evaluatedAt: string;
  validFrom: string;
  validUntil: string;
  qualityGate: string;
  spatialGate: string;
  temporalGate: string;
  evidenceDigest: string;
  traceId: string;
  ruleId: string;
  ruleVersion: number;
  approvalState: string;
  metrics?: Forecast['metrics'];
  thresholdsFromApprovedRule?: EvidenceRule | null;
}
export interface EvidenceRule {
  id: string;
  version: number;
  sourceMode: string;
  regionId: string;
  scenarioId: string | null;
  owner: string;
  approvalState: string;
  enabled: boolean;
  validFrom: string;
  validUntil: string;
  applicability: string;
  metric: string;
  unit: string;
  minimum: number | null;
  maxAgeSeconds: number;
  windowSeconds: number;
  revisionReason: string;
}
export interface Attention {
  id: string;
  inputMode: string;
  regionId: string;
  version: number;
  state: string;
  evaluationId: string;
  validUntil: string;
  ruleId?: string;
  ruleVersion?: number;
  createdAt?: string;
  updatedAt?: string;
  audit: {
    version: number;
    at: string;
    actor: string;
    action: string;
    reason: string;
    evaluationId: string;
  }[];
}
export interface ForecastSnapshot {
  sourceMode: 'LIVE' | 'DEMO';
  generatedAt: string;
  forecasts: Forecast[];
  evaluations: Evaluation[];
  attentions: Attention[];
  rules: {
    id: string;
    version: number;
    sourceMode: string;
    regionId: string;
    owner: string;
    enabled: boolean;
    approvalState: string;
  }[];
  health: {
    regionId: string;
    status: string;
    error: string | null;
    retrievedAt: string | null;
    nextAttemptAt: string;
  }[];
  officialAlerts: string;
  limitations: string[];
}
const object = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const time = (v: unknown) => typeof v === 'string' && Number.isFinite(Date.parse(v));
const strings = (v: unknown) => Array.isArray(v) && v.every((x) => typeof x === 'string');
const hash = (v: unknown) => typeof v === 'string' && /^[a-f0-9]{64}$/.test(v);
export function parseForecast(value: unknown, mode: 'LIVE' | 'DEMO'): ForecastSnapshot {
  if (
    !object(value) ||
    value.sourceMode !== mode ||
    !time(value.generatedAt) ||
    !['forecasts', 'rules', 'evaluations', 'attentions', 'health'].every((k) =>
      Array.isArray(value[k]),
    ) ||
    value.officialAlerts !== 'NOT_CONNECTED' ||
    !strings(value.limitations)
  )
    throw new Error('FORECAST_ENVELOPE');
  for (const f of value.forecasts as unknown[]) {
    if (
      !object(f) ||
      f.sourceMode !== mode ||
      f.factType !== 'WEATHER_FORECAST' ||
      !object(f.region) ||
      typeof f.region.regionId !== 'string' ||
      typeof f.region.displayName !== 'string' ||
      typeof f.region.latitude !== 'number' ||
      !Number.isFinite(f.region.latitude) ||
      Math.abs(f.region.latitude) > 90 ||
      typeof f.region.longitude !== 'number' ||
      !Number.isFinite(f.region.longitude) ||
      Math.abs(f.region.longitude) > 180 ||
      typeof f.id !== 'string' ||
      !hash(f.rawSha256) ||
      !hash(f.discoverySha256) ||
      !['issuedAt', 'validFrom', 'validUntil', 'retrievedAt'].every((k) => time(f[k])) ||
      Date.parse(String(f.issuedAt)) > Date.parse(String(f.retrievedAt)) ||
      Date.parse(String(f.validUntil)) <= Date.parse(String(f.validFrom)) ||
      f.spatialType !== 'GRID_FORECAST' ||
      !Array.isArray(f.polygon) ||
      f.polygon.length < 4 ||
      !f.polygon.every(
        (p) =>
          Array.isArray(p) &&
          p.length === 2 &&
          p.every((n) => typeof n === 'number' && Number.isFinite(n)) &&
          Math.abs(p[0]) <= 180 &&
          Math.abs(p[1]) <= 90,
      ) ||
      JSON.stringify(f.polygon[0]) !== JSON.stringify(f.polygon.at(-1)) ||
      !Array.isArray(f.metrics) ||
      !f.metrics.every(
        (m) =>
          object(m) &&
          typeof m.name === 'string' &&
          typeof m.unit === 'string' &&
          typeof m.quality === 'string' &&
          (m.value === null || (typeof m.value === 'number' && Number.isFinite(m.value))),
      ) ||
      (mode === 'LIVE' &&
        (f.provider !== 'NOAA_NWS' ||
          typeof f.endpoint !== 'string' ||
          !/^https:\/\/api\.weather\.gov\/gridpoints\/[A-Z]{3,4}\/\d+,\d+\/forecast\/hourly$/.test(
            f.endpoint,
          ))) ||
      (mode === 'DEMO' && f.provider !== 'DEMO_FIXTURE')
    )
      throw new Error('FORECAST_RECORD');
  }
  for (const e of value.evaluations as unknown[]) {
    if (
      !object(e) ||
      e.inputMode !== mode ||
      typeof e.id !== 'string' ||
      typeof e.regionId !== 'string' ||
      !['NOT_EVALUATED', 'INSUFFICIENT_DATA', 'NO_MATCH', 'ATTENTION'].includes(String(e.result)) ||
      !strings(e.forecastIds) ||
      !strings(e.observationIds) ||
      !strings(e.sourceLineage) ||
      !hash(e.evidenceDigest) ||
      !['evaluatedAt', 'validFrom', 'validUntil'].every((k) => time(e[k])) ||
      (mode === 'LIVE' && e.result === 'ATTENTION')
    )
      throw new Error('FORECAST_EVALUATION');
  }
  for (const a of value.attentions as unknown[]) {
    if (
      !object(a) ||
      mode !== 'DEMO' ||
      a.inputMode !== mode ||
      typeof a.id !== 'string' ||
      !Number.isSafeInteger(a.version) ||
      !time(a.validUntil) ||
      !Array.isArray(a.audit) ||
      !a.audit.every(
        (r) =>
          object(r) &&
          time(r.at) &&
          Number.isSafeInteger(r.version) &&
          typeof r.actor === 'string' &&
          typeof r.reason === 'string',
      ) ||
      ![
        'INTERNAL_ATTENTION',
        'ACKNOWLEDGED',
        'REVIEWED',
        'DISMISSED',
        'RECOVERED',
        'EXPIRED',
        'SUSPENDED',
      ].includes(String(a.state))
    )
      throw new Error('FORECAST_ATTENTION');
  }
  for (const r of value.rules as unknown[])
    if (
      !object(r) ||
      r.sourceMode !== mode ||
      typeof r.owner !== 'string' ||
      !Number.isSafeInteger(r.version) ||
      (mode === 'LIVE' && (r.enabled !== false || r.approvalState !== 'NOT_APPROVED'))
    )
      throw new Error('FORECAST_RULE');
  for (const h of value.health as unknown[])
    if (
      !object(h) ||
      typeof h.regionId !== 'string' ||
      !['CONNECTED', 'DEMO_FIXTURE', 'STALE', 'UNAVAILABLE'].includes(String(h.status)) ||
      !time(h.nextAttemptAt)
    )
      throw new Error('FORECAST_HEALTH');
  return value as unknown as ForecastSnapshot;
}
export function readForecast(mode: 'LIVE' | 'DEMO', signal: AbortSignal) {
  return createMonitoringRequest({ baseOrigin: apiBaseOrigin() })(
    `/read-models/normal-monitoring/weather-attention?sourceMode=${mode}`,
    (v) => parseForecast(v, mode),
    signal,
  );
}
export function evidenceUrl(id: string) {
  return `${apiBaseOrigin()}/read-models/normal-monitoring/weather-attention?evidenceId=${encodeURIComponent(id)}`;
}
export function auditUrl(id: string) {
  return `${apiBaseOrigin()}/read-models/normal-monitoring/weather-attention?attentionId=${encodeURIComponent(id)}`;
}
